import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  TextField,
  IconButton,
  Button as MuiButton,
  Chip,
  InputAdornment,
} from '@mui/material';
import { Trash2, Plus, Briefcase, Zap, Wand2 } from 'lucide-react';
import { ADMIN_PAGES_KEYS, NS } from '../../../../../../../i18n/constants/keys';
import { Button, Drawer } from '../../../../../../../ui';
import { FloatingLabelSelect } from '../../../../../../../ui/components/Common/TextFields/OutlinedSelect';
import {
  AutoAssessment,
  AutoBuildCompleteStatus,
} from '../../../../../../../utils/types/JobOpenings';
import { useQuickBuild } from './Hooks';

interface IQuickBuild {
  isOpen: boolean;
  onCloseDrawer: () => void;
  assessmentId: string;
  autoAssessment: AutoAssessment;
  setAutoAssessment: React.Dispatch<React.SetStateAction<AutoAssessment>>;
  skills: string[];
  jobTitle: string;
  completedElement?: () => void;
  onGenerationComplete?: () => void;
}

export const QuickBuildDrawer: React.FC<IQuickBuild> = (props) => {
  const {
    isOpen,
    onCloseDrawer,
    autoAssessment,
    setAutoAssessment,
    skills,
    jobTitle,
    completedElement,
    onGenerationComplete,
  } = props;
  const {
    records,
    isSaving,
    generatingQuestions,
    hasUnsavedChanges,
    skillsList,
    questionTypesList,
    levels,
    editedJobTitle,
    handleRemoveSelected,
    handleAddAllSuggestions,
    handleToggleSelectAll,
    handleAddSkill,
    generateQuestions,
    saveSkills,
    handleRemoveSkill,
    handleSelectChange,
    handleInputChange,
    addNewSkillRow,
    setRecords,
    setEditedJobTitle,
  } = useQuickBuild({
    autoAssessment,
    setAutoAssessment,
    skills,
    jobTitle,
    completedElement,
    onGenerationComplete,
  });
  const { t } = useTranslation(NS.adminPages);
  const QB = ADMIN_PAGES_KEYS.jobOpenings.quickBuild;

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onCloseDrawer}
      width="w-full lg:max-w-[94vw]"
      title={t(QB.drawerTitle)}
      footerContent={
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { sm: 'center' },
            justifyContent: 'space-between',
            gap: 2,
            bgcolor: 'grey.50',
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
            <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>
              {records.length}
            </Box>{' '}
            {t(QB.skillsConfigured)} • {t(QB.totalPoints)}{' '}
            <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>
              {records.reduce((acc, r) => acc + Number(r.points || 0), 0)}
            </Box>
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5, justifyContent: { sm: 'flex-end' } }}>
            <Button
              disabled={isSaving || generatingQuestions}
              label={t(QB.close)}
              onClick={onCloseDrawer}
              variant="secondary"
              sx={{ width: { xs: "100%", sm: "auto" }, px: 2.5, borderColor: "#d1d5db", color: "#4b5563", bgcolor: "#ffffff", "&:hover": { color: "#1f2937", bgcolor: "#f9fafb" } }}
            />
          </Box>
        </Box>
      }
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: 'grey.50' }}>
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            flexDirection: { xs: 'column', lg: 'row' },
            gap: { xs: 2, sm: 3 },
            height: '100%',
          }}
        >
          {/* LEFT PANEL: Table */}
          <Box sx={{ width: { xs: '100%', lg: '75%' }, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Paper
              variant="outlined"
              sx={{
                borderRadius: 2,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                flexGrow: 1,
                bgcolor: 'background.paper',
              }}
            >
              {/* Table Header / Toolbar */}
              <Box
                sx={{
                  px: { xs: 2, sm: 3 },
                  py: 2,
                  borderBottom: 1,
                  borderColor: 'divider',
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  justifyContent: 'space-between',
                  alignItems: { sm: 'center' },
                  gap: 1.5,
                  bgcolor: 'background.paper',
                  position: 'sticky',
                  top: 0,
                  zIndex: 1,
                }}
              >
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 4, height: 20, bgcolor: 'primary.main', borderRadius: 1 }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary' }}>
                      {t(QB.skillConfiguration)}
                    </Typography>
                  </Box>
                  {records.some((r) => r.checked) && (
                    <MuiButton
                      size="small"
                      color="error"
                      variant="outlined"
                      startIcon={<Trash2 size={14} />}
                      onClick={handleRemoveSelected}
                      sx={{ textTransform: 'none', borderRadius: 4, py: 0.25, px: 1.5, fontSize: '12px' }}
                    >
                      {t(QB.removeSelected)} ({records.filter((r) => r.checked).length})
                    </MuiButton>
                  )}
                </Box>
                {hasUnsavedChanges && (
                  <Chip
                    label={t(QB.unsavedChanges)}
                    size="small"
                    color="warning"
                    variant="outlined"
                    sx={{ fontWeight: 600, fontSize: '11px' }}
                  />
                )}
              </Box>

              {/* Scrollable Table Area */}
              <Box sx={{ overflow: 'auto', flexGrow: 1, px: { xs: 1.5, sm: 3 }, py: 1 }}>
                {records.length > 0 ? (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox" align="center">
                            <Checkbox
                              size="small"
                              checked={records.length > 0 && records.every((r) => r.checked)}
                              onChange={handleToggleSelectAll}
                            />
                          </TableCell>
                          <TableCell sx={{ width: '25%', fontWeight: 600 }}>{t(QB.colSkill)}</TableCell>
                          <TableCell sx={{ width: '18%', fontWeight: 600 }}>{t(QB.colLevel)}</TableCell>
                          <TableCell align="center" sx={{ width: 90, fontWeight: 600 }}>
                            {t(QB.colQs)}
                          </TableCell>
                          <TableCell align="center" sx={{ width: 90, fontWeight: 600 }}>
                            {t(QB.colPoints)}
                          </TableCell>
                          <TableCell align="center" sx={{ width: 90, fontWeight: 600 }}>
                            {t(QB.colTime)}
                          </TableCell>
                          <TableCell sx={{ width: '20%', fontWeight: 600 }}>{t(QB.colType)}</TableCell>
                          <TableCell align="center" sx={{ width: 40 }} />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {records.map((record, index) => (
                          <TableRow key={index} hover>
                            <TableCell padding="checkbox" align="center">
                              <Checkbox
                                size="small"
                                checked={record.checked || false}
                                onChange={() => {
                                  const n = [...records];
                                  n[index].checked = !n[index].checked;
                                  setRecords(n);
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                size="small"
                                fullWidth
                                variant="standard"
                                value={record.skill}
                                onChange={handleInputChange(index, 'skill')}
                                placeholder="e.g. React"
                                InputProps={{ disableUnderline: true }}
                              />
                            </TableCell>
                            <TableCell>
                              <FloatingLabelSelect
                                value={levels.find((l) => l.value === record.level)}
                                onChange={handleSelectChange(index, 'level')}
                                options={levels}
                                placeholder="Select Level"
                              />
                            </TableCell>
                            <TableCell align="center">
                              <TextField
                                size="small"
                                type="number"
                                variant="standard"
                                value={record.numberOfQuestions}
                                onChange={handleInputChange(index, 'numberOfQuestions')}
                                InputProps={{ disableUnderline: true }}
                                inputProps={{ style: { textAlign: 'center' } }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <TextField
                                size="small"
                                type="number"
                                variant="standard"
                                value={record.points}
                                onChange={handleInputChange(index, 'points')}
                                InputProps={{ disableUnderline: true }}
                                inputProps={{ style: { textAlign: 'center' } }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <TextField
                                size="small"
                                type="number"
                                variant="standard"
                                value={record.time}
                                onChange={handleInputChange(index, 'time')}
                                InputProps={{ disableUnderline: true }}
                                inputProps={{ style: { textAlign: 'center' } }}
                              />
                            </TableCell>
                            <TableCell>
                              <FloatingLabelSelect
                                value={questionTypesList.find((t) => t.value === record.questionType)}
                                onChange={handleSelectChange(index, 'questionType')}
                                options={questionTypesList}
                                placeholder="Select Type"
                              />
                            </TableCell>
                            <TableCell align="center">
                              <IconButton
                                size="small"
                                onClick={() => handleRemoveSkill(index)}
                                sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}
                              >
                                <Trash2 size={16} />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minHeight: 220,
                      textAlign: 'center',
                      borderRadius: 3,
                      border: '2px dashed',
                      borderColor: 'divider',
                      m: { xs: 2, sm: 3 },
                      p: 4,
                      bgcolor: 'action.hover',
                    }}
                  >
                    <Box
                      sx={{
                        p: 2,
                        bgcolor: 'background.paper',
                        borderRadius: '50%',
                        boxShadow: 1,
                        mb: 2,
                      }}
                    >
                      <Plus size={32} color="#9e9e9e" />
                    </Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                      {t(QB.noSkillsTitle)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 260, mt: 0.5, mb: 2 }}>
                      {t(QB.noSkillsHint)}
                    </Typography>
                    <Button
                      variant="primary"
                      label={t(QB.addFirstSkill)}
                      onClick={addNewSkillRow}
                      icon={<Plus size={16} />}
                      hasIcon
                    />
                  </Box>
                )}
              </Box>

              {/* Table Footer Actions */}
              <Box
                sx={{
                  px: { xs: 2, sm: 3 },
                  py: 2,
                  borderTop: 1,
                  borderColor: 'divider',
                  bgcolor: 'action.hover',
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { sm: 'center' },
                  justifyContent: 'space-between',
                  gap: 1.5,
                }}
              >
                <Button
                  variant="link"
                  label={t(QB.addAnotherSkill)}
                  onClick={addNewSkillRow}
                  sx={{ width: { xs: "100%", sm: "auto" }, color: "#2563eb", fontWeight: 600, p: 0, "&:hover": { color: "#1d4ed8" } }}
                />
                <Button
                  variant="primary"
                  label={isSaving ? t(QB.saving) : t(QB.saveSkills)}
                  onClick={saveSkills}
                  disabled={isSaving || !hasUnsavedChanges || generatingQuestions}
                  sx={{ width: { xs: "100%", sm: "auto" }, boxShadow: 1 }}
                />
              </Box>
            </Paper>
          </Box>

          {/* RIGHT PANEL: Sidebar */}
          <Box sx={{ width: { xs: '100%', lg: '25%' }, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Settings Card */}
            <Paper variant="outlined" sx={{ borderRadius: 2, p: { xs: 2, sm: 2.5 } }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', mb: 2 }}>
                {t(QB.configuration)}
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}>
                    {t(QB.jobTitle)}
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    value={editedJobTitle}
                    onChange={(e) => setEditedJobTitle(e.target.value)}
                    placeholder="e.g. Frontend Developer"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Briefcase size={16} color="#757575" />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    {t(QB.jobTitleHint)}
                  </Typography>
                </Box>

                <Box sx={{ pt: 2, borderTop: 1, borderColor: 'divider' }}>
                  <Button
                    variant="secondary"
                    label={generatingQuestions ? t(QB.generating) : t(QB.autoGenerate)}
                    onClick={generateQuestions}
                    disabled={
                      generatingQuestions ||
                      isSaving ||
                      records.length === 0 ||
                      hasUnsavedChanges ||
                      autoAssessment?.status === AutoBuildCompleteStatus
                    }
                    sx={{ width: "100%", justifyContent: "center", py: 1.25, bgcolor: "#eff6ff", color: "#1d4ed8", border: "1px solid #dbeafe", "&:hover": { bgcolor: "#dbeafe", borderColor: "#bfdbfe" }, boxShadow: 1, opacity: generatingQuestions ? 0.7 : 1, transition: "all 0.2s ease", fontWeight: 500 }}
                    icon={!generatingQuestions && <Wand2 size={16} />}
                    hasIcon
                  />
                </Box>
              </Box>
            </Paper>

            {/* Suggestions Panel */}
            <Paper
              variant="outlined"
              sx={{
                borderRadius: 2,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                flexGrow: 1,
                maxHeight: { xs: 320, sm: 400 },
              }}
            >
              <Box
                sx={{
                  p: 2,
                  borderBottom: 1,
                  borderColor: 'divider',
                  bgcolor: 'action.hover',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Zap size={16} color="#9c27b0" />
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                    {t(QB.suggestedSkills)}
                  </Typography>
                </Box>
                {skillsList.length > 0 && (
                  <MuiButton
                    size="small"
                    onClick={handleAddAllSuggestions}
                    sx={{ textTransform: 'none', borderRadius: 4, fontSize: '11px', py: 0.25, px: 1 }}
                  >
                    {t(QB.selectAll)}
                  </MuiButton>
                )}
              </Box>

              <Box sx={{ p: 1, overflowY: 'auto', flexGrow: 1 }}>
                {skillsList.length > 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {skillsList.map((skill) => (
                      <Box
                        component="button"
                        type="button"
                        key={skill}
                        onClick={() => handleAddSkill(skill)}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          p: 1.5,
                          borderRadius: 1.5,
                          border: 'none',
                          bgcolor: 'transparent',
                          cursor: 'pointer',
                          textAlign: 'left',
                          transition: 'all 0.2s',
                          '&:hover': {
                            bgcolor: 'primary.50',
                            '& .skill-text': { color: 'primary.main' },
                            '& .skill-icon': { bgcolor: 'primary.light', color: 'primary.dark' },
                          },
                        }}
                      >
                        <Typography
                          variant="body2"
                          className="skill-text"
                          sx={{ fontWeight: 500, color: 'text.primary', transition: 'color 0.2s' }}
                        >
                          {skill}
                        </Typography>
                        <Box
                          className="skill-icon"
                          sx={{
                            width: 20,
                            height: 20,
                            borderRadius: '50%',
                            bgcolor: 'action.hover',
                            color: 'text.secondary',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.2s',
                          }}
                        >
                          <Plus size={12} />
                        </Box>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
                    <Typography variant="body2">{t(QB.noSuggestions)}</Typography>
                  </Box>
                )}
              </Box>

              <Box sx={{ p: 1.5, bgcolor: 'action.hover', borderTop: 1, borderColor: 'divider', textAlign: 'center' }}>
                <Typography
                  variant="caption"
                  color="primary"
                  sx={{ cursor: 'pointer', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}
                >
                  {t(QB.refreshSuggestions)}
                </Typography>
              </Box>
            </Paper>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
};

export default QuickBuildDrawer;
